import { Component, EventEmitter, Input, Output, SimpleChange, SimpleChanges } from "@angular/core";
import { IJourney } from "@app/interfaces/IJourney";
import { IPeripheral } from "@app/interfaces/IPeripheral";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IVehiclePeripheralMapping } from "@app/interfaces/IVehiclePeripheralMapping";
import { IVehicleWithCameraMappings } from "@app/main/components/resourceManagement/cameras/camera-vehicles-manager/camera-vehicles-manager.component";
import { AccountFeatureService, UserRole } from "@app/services/accountFeature.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { JourneyService } from "@app/services/journey.service";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { from, of } from "rxjs";
import { finalize, switchMap, tap } from "rxjs/operators";
import { VideoStreamType } from "../../enums/VideoStreamType";
import { IAvailableFootageCalendar } from "../../models/IAvailableFootageCalendar";
import { IVideoStream } from "../../models/ICameraChannel";
import { CameraFootageService } from "../../services/camera-footage.service";
import { CameraService } from "../../services/camera.service";
import './request-video-form.component.scss';
import { CalendarDirection } from "@app/enum/CalendarDirection.enum";
import { PeripheralService } from "@app/main/modules/foundation/_services/peripheral.service";
import { IAvailableDailyFootage } from "../../models/IAvailableDailyFootage";
import { CurrentUserService } from "@app/services/currentUser.service";
import { UserType } from "@app/enum/UserType";
import { IUser } from "@app/interfaces/IUser";

interface IRequestableVideoLength {
    lengthInSeconds: number;
    label: string;
}

@UntilDestroy()
@Component({
    selector: 'request-video-form',
    template: require('./request-video-form.component.html')
})
export class RequestVideoFormComponent {
    @Input() requestDate: Date;
    @Input() cameraDevice: IPeripheral;
    @Input() vehicle: IVehicle;
    @Input() footageLoading: boolean;
    @Input() qualitiesAvailable: VideoStreamType[];

    @Output() requestDateChange: EventEmitter<Date> = new EventEmitter<Date>();
    @Output() videoLengthChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() selectedChannelsChange: EventEmitter<IVideoStream[]> = new EventEmitter<IVideoStream[]>();
    @Output() downloadQualityChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() noAvailableDates: EventEmitter<void> = new EventEmitter<void>();
    @Output() footageHistoryLoaded: EventEmitter<IAvailableDailyFootage[]> = new EventEmitter<IAvailableDailyFootage[]>();
    @Output() journeysLoaded: EventEmitter<IJourney[]> = new EventEmitter<IJourney[]>();

    selectableDates: Date[] = [];
    minDate: Date;
    requestHour: number;
    requestMinute: number;
    requestSecond: number;

    requestableVideoLengths: IRequestableVideoLength[];
    customVideoLengthEnabled: boolean;
    videoLength: number;

    selectedChannels: IVideoStream[];
    streamingChannels: IVideoStream[];
    showChannelsEditor: boolean;
    loadingChannels: boolean;

    isLiveStreamCamera: boolean;
    latestCalendarFromFtCloud: IAvailableFootageCalendar;
    latestFootageHistoryDateQueried: Date;
    requestDateSameAsLatestQuery: boolean;

    journeys: IJourney[];

    calendarLoading: boolean;
    noFootageAvailable: boolean;
    calendarUnavailable: boolean;

    mainstreamSelected: boolean;
    selectedStreamType: VideoStreamType;

    timeChangeDebounce: ReturnType<typeof setTimeout>;

    endTime: Date;

    CalendarDirection = CalendarDirection;

    user: IUser;

    constructor(
        private readonly accountFeatureService: AccountFeatureService,
        private readonly cameraFootageService: CameraFootageService,
        private readonly cameraService: CameraService,
        private readonly dateService: DateService,
        private readonly journeyService: JourneyService,
        private readonly navigationService: NavigationService,
        private readonly translationService: TranslationService,
        private readonly peripheralService: PeripheralService,
        private readonly currentUserService: CurrentUserService) { }

    ngOnInit(): void {
        this.initialise();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['requestDate'] && this.requestTimeHasChanged(changes['requestDate'])) {

            this.setTime();
        }

        if (changes['qualitiesAvailable']) {

            this.setQuality();
        }
    }

    ngOnDestroy(): void {
        if (this.timeChangeDebounce) {
            clearTimeout(this.timeChangeDebounce);
        }
    }

    get onlineStatusAllowsRequesting(): boolean {
        if (!this.vehicle) {
            return false;
        }

        return !!this.vehicle.onlineStatus
            && ((this.vehicle.onlineStatus.online && this.vehicle.onlineStatus.hasStreaming) || (!this.vehicle.onlineStatus.hasStreaming));
    }

    get showCalendar(): boolean {
        return !this.calendarUnavailable && !this.noFootageAvailable;
    }

    get canEditChannels(): boolean {
        const isAdmin = this.accountFeatureService.currentUserHasRole([UserRole.Administrator]);
        return isAdmin || this.user?.Type == UserType.InternalUser;
    }

    onDateChanged(newDate: Date): void {
        this.navigationService.setRouteParam("requestVideoTime", null, false);
        this.requestDate = newDate;
        this.emitDateChange();
    }

    onSelectedTimeChanged(): void {
        if (this.timeIsNullOrNaN()) {
            return;
        }

        clearTimeout(this.timeChangeDebounce);

        this.timeChangeDebounce = setTimeout(() => {
            // Debounce to allow for scrolling through units quickly
            const timeString = this.convertTimeToString();
            const updatedDate = this.dateService.updateTime(this.requestDate, timeString);
            this.requestDate = updatedDate;

            this.emitDateChange();
        }, 500)
    }

    onVideoLengthSelected(length: number): void {
        this.videoLength = length;
        this.endTime = this.dateService.addOrSubtractTime(this.requestDate, this.videoLength, DateUnit.Sec);
        this.emitVideoLengthChange();
    }

    onSelectedChannelsChanged(channels: IVideoStream[]): void {
        this.selectedChannels = channels;
        this.emitChannelsChange(channels);
    }

    onDownloadQualityChanged(quality: VideoStreamType): void {
        this.selectedStreamType = quality;
        this.mainstreamSelected = quality == VideoStreamType.Main;
        this.downloadQualityChange.emit(this.mainstreamSelected);
    }

    openChannelsEditor(): void {
        this.showChannelsEditor = true;
    }

    closeChannelsEditor(): void {
        this.showChannelsEditor = false;
    }

    onVehicleSaved(vehicle: IVehicleWithCameraMappings): void {
        this.loadingChannels = true;
        this.closeChannelsEditor();

        setTimeout(() => {
            this.getPeripheralChannels();
            this.loadingChannels = false;
        }, 800)
    }

    //--

    private initialise(): void {
        this.customVideoLengthEnabled = this.cameraFootageService.isFootageLengthFunctionalityAvailable([this.cameraDevice]);
        this.isLiveStreamCamera = this.cameraDevice.Product.ProductMetadata?.CameraFeatures?.VideoRequest?.OnlineOnly;

        this.getMinDateFromLatestPulseRecord();

        this.setupRequestableVideoLengths();
        this.getPeripheralChannels();

        this.currentUserService.getCurrentUser().then(currentUser => {
            this.user = currentUser;
        });
    }

    private getMinDateFromLatestPulseRecord(): void {
        this.peripheralService.getPulseRecords([this.cameraDevice])
            .subscribe({
                next: (record) => {
                    if (record?.length > 0 && record[0]?.cameraFootageStart) {
                        this.minDate = new Date(record[0].cameraFootageStart);
                    }
                },
                complete: () => this.getAvailableDates()
            });
    }

    private timeIsNullOrNaN(): boolean {
        if (this.requestHour == null || this.requestMinute == null || this.requestSecond == null) {
            return true;
        }

        if (isNaN(this.requestHour) || isNaN(this.requestMinute) || isNaN(this.requestSecond)) {
            return true;
        }

        return false;
    }

    private convertTimeToString(): string {
        if (this.timeIsNullOrNaN()) {
            return '00:00:00';
        }

        const hourString = this.requestHour < 10 ? `0${this.requestHour}` : String(this.requestHour);
        const minuteString = this.requestMinute < 10 ? `0${this.requestMinute}` : String(this.requestMinute);
        const secondString = this.requestSecond < 10 ? `0${this.requestSecond}` : String(this.requestSecond);

        return `${hourString}:${minuteString}:${secondString}`;
    }

    private setTime(): void {
        if (!this.requestDate) {
            return;
        }

        const timeString = this.dateService.getTimeAsString(this.requestDate, true);
        const hoursMinutesSeconds: string[] = timeString.split(':');

        this.requestHour = Number(hoursMinutesSeconds[0]);
        this.requestMinute = Number(hoursMinutesSeconds[1]);
        this.requestSecond = Number(hoursMinutesSeconds[2]);

        this.endTime = this.dateService.addOrSubtractTime(this.requestDate, this.videoLength, DateUnit.Sec);
    }

    private setQuality(): void {
        if (!this.qualitiesAvailable?.length || (this.selectedStreamType && this.qualitiesAvailable.includes(this.selectedStreamType))) {
            return;
        }

        this.mainstreamSelected = !this.qualitiesAvailable.includes(VideoStreamType.Sub) || this.qualitiesAvailable.length > 1;
    }

    private setupRequestableVideoLengths(): void {
        if (!this.cameraDevice) {
            return;
        }

        this.addRequestableVideoLengths(this.cameraFootageService.getMaxRequestFootageLength(this.cameraDevice));

        if (!this.customVideoLengthEnabled) {
            return;
        }

        this.requestableVideoLengths.sort((videoLength1, videoLength2) => videoLength2.lengthInSeconds - videoLength1.lengthInSeconds);

        this.videoLength = this.requestableVideoLengths[this.requestableVideoLengths.length - 1].lengthInSeconds;
        this.emitVideoLengthChange();
    }

    private updateSelectableDatesFromAvailableFootage(sections: IAvailableDailyFootage[]): void {        
        this.selectableDates = [];
        const uniqueDates = new Set<string>();
        
        sections.forEach(section => {                   
            uniqueDates.add(new Date(section.Start).toDateString());
        });            
        
        uniqueDates.forEach(date => {
            this.selectableDates.push(new Date(date));
        });
    }

    private async getAvailableDates(): Promise<void> {
        if (this.minDate) {           
            
            const history = await this.cameraFootageService.getCameraFootageHistory(this.cameraDevice.Serial, this.minDate);           
            const sections = this.cameraFootageService.mapFootageHistoryToAvailableDailyFootage(history);            

            this.updateSelectableDatesFromAvailableFootage([...sections]);

            if (this.selectableDates.length > 0) {
                this.emitFootageHistory(sections.map(item => ({
                    ...item,
                    Start: new Date(item.Start),
                    End: new Date(item.End)
                })));
            } else {
                this.noFootageAvailable = true;
                this.handleNoAvailableDates();
            }

            return;
        }

        if (this.isLiveStreamCamera) {
            this.setRequestableDatesFromFtCloudFootage();
            return;
        }

        const startDate = this.dateService.addOrSubtractTime(new Date(), -1, DateUnit.Week);
        const endDate = new Date();

        from(this.journeyService.getJourneysForVehicle(this.vehicle.Id, startDate, endDate) as Promise<IJourney[]>).pipe(
            untilDestroyed(this),
            switchMap((journeys: IJourney[]) => {

                this.journeys = journeys;

                return from(this.journeyService.getCurrentJourney(this.vehicle.Id, true) as Promise<IJourney>)
            }),
            switchMap((journey: IJourney) => {
                if (journey) {
                    this.journeys.push(journey);
                }
                this.updateSelectableDatesFromJourneys();

                return from(this.cameraService.getPeripheralMappings(this.cameraDevice.Serial));
            }),
            tap((mapping: IVehiclePeripheralMapping) => {
                this.selectableDates = this.selectableDates.filter(date => this.dateService.date1IsAfterDate2(date, mapping.StartUtc));

                this.setRequestDate();

                this.handleNoAvailableDates();
            })
        ).subscribe(() => {
            this.emitDateChange();
            this.journeysLoaded.emit(this.journeys);

        })

    }

    private updateSelectableDatesFromJourneys(): void {
        if (!this.journeys?.length) {
            return;
        }

        this.journeys.sort((journey1, journey2) => {
            return new Date(journey2.StartUtc).getTime() - new Date(journey1.StartUtc).getTime();
        });

        for (const journey of this.journeys) {
            const journeyStartTime: Date = new Date(journey.StartUtc);
            const journeyEndTime: Date = new Date(journey.EndUtc);

            this.selectableDates.push(journeyStartTime);

            if (this.dateService.isSameDate(journeyStartTime, journeyEndTime)) {
                continue;
            }

            this.selectableDates.push(journeyEndTime);
        }
    }

    private setRequestableDatesFromFtCloudFootage(): void {
        let dailyFootageDate: Date;
        this.calendarLoading = true;

        from(this.cameraFootageService.getLatestCalendarFootageHistory(this.cameraDevice.Serial, true, null, true)).pipe(
            untilDestroyed(this),
            finalize(() => this.calendarLoading = false),
            switchMap((latestFootageCalendar: IAvailableFootageCalendar) => {
                this.latestCalendarFromFtCloud = latestFootageCalendar;
                dailyFootageDate = this.getDailyFootageDate();

                this.clearLatestFootageHistoryDateQueried();

                if (!dailyFootageDate) {
                    return of(null);
                }

                this.updateLatestQueriedDate(dailyFootageDate);
                this.updateSelectableDatesFromFtCloudCalendar(this.latestCalendarFromFtCloud);

                return from(this.cameraService.getPeripheralMappings(this.cameraDevice.Serial));
            }),
            tap((mapping: IVehiclePeripheralMapping) => {
                this.selectableDates = this.selectableDates.filter(date => this.dateService.date1IsAfterDate2(date, mapping.StartUtc));

                this.setRequestDate();

                this.handleNoAvailableDates();
            })
        ).subscribe(
            () => {
                this.emitDateChange();
            },
            error => {
                this.calendarUnavailable = true;
            });
    }

    private setRequestDate(): void {
        const presetTime = this.navigationService.getRouteParam("requestVideoTime");
        this.requestDate = new Date(presetTime ? presetTime : this.selectableDates[0]);
    }

    private handleNoAvailableDates(): void {
        const today = new Date();
        const todayExists: boolean = !!this.selectableDates.find(date => this.dateService.isSameDate(date, today));

        if (todayExists || this.selectableDates?.length) {
            return;
        }

        this.noFootageAvailable = true;
        this.noAvailableDates.emit();
    }

    private updateSelectableDatesFromFtCloudCalendar(latestCalendarFromFtCloud: IAvailableFootageCalendar): void {
        this.selectableDates = [];
        if (!latestCalendarFromFtCloud?.AvailableDates?.length) {
            return;
        }

        for (const calendarDay of latestCalendarFromFtCloud.AvailableDates) {
            this.selectableDates.push(calendarDay);
        }
    }

    private getDailyFootageDate(): Date {
        if (!this.latestCalendarFromFtCloud?.AvailableDates?.length) {
            return;
        }

        this.latestCalendarFromFtCloud.AvailableDates.sort((date1, date2) => {
            return new Date(date2).getTime() - new Date(date1).getTime();
        });

        let dailyFootageDate = new Date(this.latestCalendarFromFtCloud.AvailableDates[0]);
        const matchingAvailableRequestDate = this.latestCalendarFromFtCloud.AvailableDates.find(date => this.dateService.isSameDate(new Date(date), this.requestDate));

        if (!!matchingAvailableRequestDate) {
            dailyFootageDate = this.requestDate;
        }

        return dailyFootageDate;
    }

    private clearLatestFootageHistoryDateQueried(): void {
        this.latestFootageHistoryDateQueried = null;
    }

    private updateLatestQueriedDate(newDate: Date): void {
        this.latestFootageHistoryDateQueried = newDate;

        this.updateLatestQueryDateCheck();
    }

    private updateLatestQueryDateCheck(): void {
        this.requestDateSameAsLatestQuery = (this.requestDate && this.latestFootageHistoryDateQueried)
            ? this.dateService.isSameDate(this.requestDate, this.latestFootageHistoryDateQueried)
            : false;
    }

    private addRequestableVideoLengths(maxLength: number): void {
        this.requestableVideoLengths = [];

        const secondsLabel = this.translationService.t("Common.Seconds");
        const secondsInitial = secondsLabel.slice(0, 1).toLowerCase();

        const minutesLabel = this.translationService.t("Common.Minutes");
        const minutesAbbr = minutesLabel.slice(0, 3);

        this.requestableVideoLengths.push({ lengthInSeconds: 20, label: "20" + secondsInitial });
        this.requestableVideoLengths.push({ lengthInSeconds: 30, label: "30" + secondsInitial });
        this.requestableVideoLengths.push({ lengthInSeconds: 60, label: "1 " + minutesAbbr });

        if (maxLength <= 60) {
            return;
        }

        this.requestableVideoLengths.push({ lengthInSeconds: 120, label: "2 " + minutesAbbr });
        this.requestableVideoLengths.push({ lengthInSeconds: 300, label: "5 " + minutesAbbr });
    }

    private getPeripheralChannels(): void {
        this.cameraService.getCameraChannels({ VehicleId: this.vehicle.Id }).then(channels => {

            const activeChannels = this.cameraService.filterDuplicateVideoStreams(channels).filter(c => c.IsActive);

            this.streamingChannels = activeChannels;
            this.selectedChannels = activeChannels;
            this.emitChannelsChange(this.selectedChannels);
        });
    }

    private requestTimeHasChanged(change: SimpleChange): boolean {
        if (!change.previousValue) {
            return true;
        }

        const currentTime = new Date(change.previousValue);
        const newTime = new Date(change.currentValue);

        return currentTime.getTime() !== newTime.getTime();
    }

    private emitDateChange(): void {
        this.requestDateChange.emit(this.requestDate);
    }

    private emitVideoLengthChange(): void {
        this.videoLengthChange.emit(this.videoLength);
    }

    private emitChannelsChange(channels: IVideoStream[]): void {
        this.selectedChannelsChange.emit(channels)
    }

    private emitFootageHistory(availableDailyFootage: IAvailableDailyFootage[]): void {
        this.footageHistoryLoaded.emit(availableDailyFootage);
    }

}
